import { useEffect, useState } from "react";

import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Stack,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  Zoom,
} from "@mui/material";

import { IconMenu2 } from "@tabler/icons";

import DatePicker from "components/datePicker";
// project imports
import LogoSection from "../logoSection";
import ProfileSection from "./ProfileSection";
import { truncateStringWithEllipsis } from "features/components/helperFunctions";
import { ellipseHiddenText } from "utils/styles";
import { useDispatch, useSelector } from "store";
import { openDrawer } from "store/slices/menu";
import { setDate } from "store/slices/Main";

// ==============================|| MAIN NAVBAR / HEADER ||============================== //
let lastActivityTimestamp = new Date().getTime();

const Header = () => {
  const isScreenSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const isLargeScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("lg")
  );
  
  const theme = useTheme();
  const dispatch = useDispatch();
  const { drawerOpen } = useSelector((state) => state.menu);
  const { showDatePicker, businessWebsite } = useSelector(
      (state) => state.global
    ), // Enable and Disable Date Picker
    { businessBranchesAccess } = useSelector((state) => state.businessBranches);
  const [triggerChange, setChange] = useState<boolean>(false);

  //======================================= Side Effects =======================================//

  useEffect(() => {
    // Event listener for visibility change
    document.addEventListener('visibilitychange', () => {
      lastActivityTimestamp = new Date().getTime();
    });

    setInterval(checkInactivity, 3600000); // 3600000 milliseconds = 1 hour
  }, [])

  useEffect(() => { }, [triggerChange])

  //======================================= Handler Functions =======================================//

  // Function to check if 24 hours have passed since last activity
  function has24HoursPassedSinceLastActivity() {
    const currentTime = new Date().getTime();
    const timeDifference = currentTime - lastActivityTimestamp;
    let twentyFourHoursInMilliseconds = 24 * 60 * 60 * 1000;
    return timeDifference > twentyFourHoursInMilliseconds;
  }

  function checkInactivity() {
    if (has24HoursPassedSinceLastActivity()) setChange(true)
  }

  const onChange = (date: any) => {
    const startDate = date[0];
    const endDate = date[1];
    dispatch(setDate({ startDate, endDate }));
  };

  return (
    <>
      <Box sx={{ display: "flex", width: "100%" }}>
        {/* logo & toggler button */}
        <Box
          sx={{
            width: 276,
            display: "flex",
            [theme.breakpoints.down("md")]: {
              width: "auto",
            },
            alignItems: "center",
          }}
        >
          <Box
            component="span"
            sx={{ display: { xs: "none", md: "block" }, flexGrow: 1 }}
          >
            <LogoSection />
          </Box>
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              overflow: "hidden",
              transition: "all .2s ease-in-out",
              background:
                theme.palette.mode === "dark"
                  ? theme.palette.dark.main
                  : theme.palette.secondary.light,
              color:
                theme.palette.mode === "dark"
                  ? theme.palette.secondary.main
                  : theme.palette.secondary.dark,
              "&:hover": {
                background:
                  theme.palette.mode === "dark"
                    ? theme.palette.secondary.main
                    : theme.palette.secondary.dark,
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.secondary.light
                    : theme.palette.secondary.light,
              },
              marginRight: isScreenSmall ? "2px" : "0px",
            }}
            onClick={() => dispatch(openDrawer(!drawerOpen))}
            color="inherit"
          >
            <IconMenu2 stroke={1.5} size="20px" />
          </Avatar>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            ml: { xs: "12px", lg: "28px" },
            position: { xs: "relative", lg: "unset" },
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: { xs: "start", lg: "center" },
              flexDirection: { xs: "column", lg: "row" },
              pr: { xs: "35px !important", lg: "0" },
            }}
          >
            {showDatePicker && <DatePicker onChange={onChange} />}

            {businessBranchesAccess.length == 1 && (
              <Stack
                direction={"row"}
                spacing={0.5}
                sx={{
                  ml: { xs: "0", lg: showDatePicker ? "16px" : "0" },
                  mt: "4px",
                }}
              >
                <Typography>Branch:</Typography>

                <Tooltip
                  title={businessBranchesAccess[0]?.label}
                  TransitionComponent={Zoom}
                >
                  <Typography
                    variant={isLargeScreen ? "h5" : "h4"}
                    sx={{
                      ...ellipseHiddenText,
                      color: "#e65b82",
                      width: { xs: "172px", md: "unset" },
                    }}
                  >
                    {truncateStringWithEllipsis(
                      businessBranchesAccess[0]?.label,
                      80
                    )}
                  </Typography>
                </Tooltip>
              </Stack>
            )}
          </Stack>
          
          <Stack   
            sx={{
            flexDirection: 'row',
            alignItems:'center',
            }}
          >
          {!isScreenSmall && businessWebsite && (
            <a
              href={`https://${businessWebsite}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              View Website
            </a>
          )}

          <Stack
            sx={{
              flexDirection: 'row',
              alignItems:'center',
              ml:'10px',
            }}
          >
            <ProfileSection />
          </Stack>
          </Stack>
        </Box>
      </Box>
    </>
  );
};

export default Header;
